import React, { useState, useCallback, useEffect } from 'react';
import {
  UserPlus, RefreshCw, CheckCircle, AlertTriangle, XCircle,
  Clock, Mail, Ban, RotateCcw, ChevronDown, ChevronUp,
  Save, Eye, Edit3, Calendar, LogIn, CreditCard
} from 'lucide-react';

// Status-Konfiguration
const statusConfig = {
  trial_aktiv: { label: 'Trial aktiv', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', icon: CheckCircle },
  unbestaetigt: { label: 'Unbestätigt', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', icon: AlertTriangle },
  abgelaufen: { label: 'Abgelaufen', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)', icon: Clock },
  kunde: { label: 'Kunde', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', icon: CreditCard },
  unbekannt: { label: 'Unbekannt', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)', icon: Clock },
};

const emailTypeLabels = {
  day3: 'Tag 3 – Check-in',
  day7: 'Tag 7 – Workflow',
  day14: 'Tag 14 – Halbzeit',
  day25: 'Tag 25 – Testende',
};

// --- Komponenten ---

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.unbekannt;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
      padding: '0.25rem 0.75rem', borderRadius: '9999px',
      fontSize: '0.8125rem', fontWeight: 600, background: cfg.bg, color: cfg.color,
    }}>
      <Icon size={14} />
      {cfg.label}
    </span>
  );
};

const EmailStatusBadge = ({ status }) => {
  const config = {
    sent: { label: 'Gesendet', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
    pending: { label: 'Ausstehend', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
    cancelled: { label: 'Storniert', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)' },
  };
  const cfg = config[status] || config.pending;
  return (
    <span style={{
      display: 'inline-flex', padding: '0.125rem 0.5rem', borderRadius: '9999px',
      fontSize: '0.75rem', fontWeight: 600, background: cfg.bg, color: cfg.color,
    }}>
      {cfg.label}
    </span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

// --- User-Karte mit Onboarding-Timeline ---
const UserCard = ({ user, onAction }) => {
  const [expanded, setExpanded] = useState(false);
  const emails = user.onboarding_emails || [];

  return (
    <div style={{
      border: '1px solid var(--border-light, #e2e8f0)',
      borderRadius: '8px',
      background: 'var(--bg-card, #ffffff)',
      marginBottom: '0.75rem',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: 0 }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'rgba(91, 124, 141, 0.1)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent-slate, #5b7c8d)', fontWeight: 700, fontSize: '0.875rem',
            flexShrink: 0,
          }}>
            {(user.fullname || '?')[0].toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
              {user.fullname || '–'}
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary, #64748b)' }}>
              {user.email} · Registriert {formatDate(user.registered_at)}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {user.last_login && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <LogIn size={12} /> {formatDate(user.last_login)}
            </span>
          )}
          <StatusBadge status={user.status} />
          {emails.length > 0 && (
            <span style={{
              fontSize: '0.75rem', color: 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', gap: '0.25rem',
            }}>
              <Mail size={12} />
              {emails.filter(e => e.status === 'sent').length}/{emails.length}
            </span>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {/* Expandierter Bereich */}
      {expanded && (
        <div style={{
          borderTop: '1px solid var(--border-light, #e2e8f0)',
          padding: '1rem 1.25rem',
          background: 'var(--bg-secondary, #f8fafc)',
        }}>
          {/* Info-Zeile */}
          <div style={{ display: 'flex', gap: '2rem', marginBottom: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            <span><Calendar size={12} style={{ marginRight: '0.25rem' }} />Trial bis: <strong>{formatDate(user.trial_ends_at)}</strong></span>
            <span>Tag {user.days_since_registration} von 30</span>
            <span>Bestätigt: {user.is_confirmed ? formatDateTime(user.confirmed_at) : <em style={{ color: '#f59e0b' }}>Nein</em>}</span>
          </div>

          {/* Onboarding-Timeline */}
          {emails.length > 0 ? (
            <div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                Onboarding-E-Mails
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {emails.map((email, idx) => (
                  <div key={email.id || `${email.email_type}-${idx}`} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem', background: 'var(--bg-card, #fff)',
                    borderRadius: '6px', border: '1px solid var(--border-light, #e2e8f0)',
                    fontSize: '0.8125rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Mail size={14} style={{ color: email.status === 'sent' ? '#10b981' : 'var(--text-secondary)' }} />
                      <span style={{ fontWeight: 500 }}>{emailTypeLabels[email.email_type] || email.email_type}</span>
                      <EmailStatusBadge status={email.status} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {email.status === 'sent' ? `Gesendet: ${formatDateTime(email.sent_at)}` : `Geplant: ${formatDateTime(email.scheduled_at)}`}
                      </span>
                      {email.status === 'pending' && email.id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onAction('cancel', { id: email.id }); }}
                          title="Stornieren"
                          style={{
                            border: 'none', background: 'none', cursor: 'pointer',
                            color: 'var(--text-secondary)', padding: '0.25rem',
                          }}
                        >
                          <Ban size={14} />
                        </button>
                      )}
                      {(email.status === 'cancelled' || email.status === 'sent') && email.id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onAction('resend', { id: email.id }); }}
                          title="Erneut einplanen"
                          style={{
                            border: 'none', background: 'none', cursor: 'pointer',
                            color: 'var(--text-secondary)', padding: '0.25rem',
                          }}
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Keine Onboarding-E-Mails in der Queue
            </div>
          )}

          {/* Aktionen */}
          {emails.some(e => e.status === 'pending') && (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                onClick={() => onAction('cancel_all', { user_sid: user.user_sid })}
                style={{
                  padding: '0.375rem 0.75rem', fontSize: '0.8125rem',
                  border: '1px solid var(--border-light, #e2e8f0)',
                  borderRadius: '6px', background: 'var(--bg-card, #fff)',
                  color: 'var(--text-secondary)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                }}
              >
                <Ban size={14} /> Alle ausstehenden stornieren
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Template Editor ---
const TemplateEditor = ({ templates, onSave, saving }) => {
  const [editTemplates, setEditTemplates] = useState(templates);
  const [activeTab, setActiveTab] = useState('day3');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    setEditTemplates(templates);
  }, [templates]);

  const handleChange = (type, field, value) => {
    setEditTemplates(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleSave = () => {
    onSave(editTemplates);
  };

  const current = editTemplates?.[activeTab];
  if (!current) return null;

  const tabs = ['day3', 'day7', 'day14', 'day25'];

  return (
    <div style={{
      border: '1px solid var(--border-light, #e2e8f0)',
      borderRadius: '8px', background: 'var(--bg-card, #ffffff)', overflow: 'hidden',
    }}>
      {/* Tab-Leiste */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--border-light, #e2e8f0)',
        background: 'var(--bg-secondary, #f8fafc)',
      }}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.75rem 1.25rem', fontSize: '0.8125rem', fontWeight: activeTab === tab ? 600 : 400,
              border: 'none', borderBottom: activeTab === tab ? '2px solid var(--accent-slate, #5b7c8d)' : '2px solid transparent',
              background: 'none', cursor: 'pointer',
              color: activeTab === tab ? 'var(--accent-slate, #5b7c8d)' : 'var(--text-secondary)',
            }}
          >
            {editTemplates[tab]?.label || tab}
          </button>
        ))}
      </div>

      <div style={{ padding: '1.25rem' }}>
        {/* Betreff */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', color: 'var(--text-primary)' }}>
            Betreff
          </label>
          <input
            type="text"
            value={current.subject || ''}
            onChange={(e) => handleChange(activeTab, 'subject', e.target.value)}
            style={{
              width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border-light, #e2e8f0)',
              borderRadius: '6px', fontSize: '0.875rem', background: 'var(--bg-card)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Headline */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', color: 'var(--text-primary)' }}>
            Headline (Anrede-Zeile)
          </label>
          <input
            type="text"
            value={current.headline || ''}
            onChange={(e) => handleChange(activeTab, 'headline', e.target.value)}
            style={{
              width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border-light, #e2e8f0)',
              borderRadius: '6px', fontSize: '0.875rem', background: 'var(--bg-card)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
          <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Inhalt (HTML)
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setPreviewMode(!previewMode)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.25rem 0.5rem', fontSize: '0.75rem',
                border: '1px solid var(--border-light, #e2e8f0)',
                borderRadius: '4px', background: previewMode ? 'var(--accent-slate, #5b7c8d)' : 'var(--bg-card)',
                color: previewMode ? '#fff' : 'var(--text-secondary)', cursor: 'pointer',
              }}
            >
              {previewMode ? <Edit3 size={12} /> : <Eye size={12} />}
              {previewMode ? 'Bearbeiten' : 'Vorschau'}
            </button>
          </div>
        </div>

        {/* Content / Preview */}
        {previewMode ? (
          <div
            style={{
              padding: '1rem', border: '1px solid var(--border-light, #e2e8f0)',
              borderRadius: '6px', background: '#fff', minHeight: '200px',
              fontSize: '0.875rem', lineHeight: '1.5',
            }}
            dangerouslySetInnerHTML={{
              __html: (current.content || '')
                .replace(/\{\{VORNAME\}\}/g, '<strong>Max</strong>')
                .replace(/\{\{BUTTON:(.*?):(.*?)\}\}/g, '<div style="margin:8px 0"><a href="$2" style="display:inline-block;padding:12px 20px;background:#002847;color:#fff;text-decoration:none;border-radius:4px;font-weight:600">$1 &rarr;</a></div>'),
            }}
          />
        ) : (
          <textarea
            value={current.content || ''}
            onChange={(e) => handleChange(activeTab, 'content', e.target.value)}
            rows={12}
            style={{
              width: '100%', padding: '0.75rem', border: '1px solid var(--border-light, #e2e8f0)',
              borderRadius: '6px', fontSize: '0.8125rem', fontFamily: 'monospace',
              resize: 'vertical', background: 'var(--bg-card)', color: 'var(--text-primary)',
              lineHeight: '1.5',
            }}
          />
        )}

        {/* Platzhalter-Hinweis */}
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          Platzhalter: <code>{'{{VORNAME}}'}</code> = Vorname · <code>{'{{BUTTON:Text:URL}}'}</code> = CTA-Button
        </div>

        {/* Speichern */}
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: 600,
              border: 'none', borderRadius: '6px',
              background: 'var(--accent-slate, #5b7c8d)', color: '#fff',
              cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1,
            }}
          >
            <Save size={16} />
            {saving ? 'Speichern...' : 'Templates speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Haupt-Komponente ---
const TrialUsersIndex = () => {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState('users'); // 'users' | 'templates'
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch('index.php/admin/getTrialUsers');
      const data = await res.json();
      if (data.users) setUsers(data.users);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch('index.php/admin/manageOnboardingEmails');
      const data = await res.json();
      if (data.templates) setTemplates(data.templates);
    } catch (err) {
      console.error('Fehler beim Laden der Templates:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadUsers(), loadTemplates()]);
    setLoading(false);
  }, [loadUsers, loadTemplates]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = async (action, params) => {
    try {
      const res = await fetch('index.php/admin/manageOnboardingEmails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...params }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadUsers();
      } else {
        showToast(data.error || 'Fehler', 'error');
      }
    } catch (err) {
      showToast('Netzwerkfehler', 'error');
    }
  };

  const handleSaveTemplates = async (updatedTemplates) => {
    setSaving(true);
    try {
      const res = await fetch('index.php/admin/manageOnboardingEmails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_templates', templates: updatedTemplates }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Templates gespeichert');
        setTemplates(updatedTemplates);
      } else {
        showToast(data.error || 'Fehler beim Speichern', 'error');
      }
    } catch (err) {
      showToast('Netzwerkfehler', 'error');
    }
    setSaving(false);
  };

  // Filter
  const filteredUsers = users.filter(u => {
    if (filter === 'all') return true;
    return u.status === filter;
  });

  const stats = {
    total: users.length,
    trial_aktiv: users.filter(u => u.status === 'trial_aktiv').length,
    unbestaetigt: users.filter(u => u.status === 'unbestaetigt').length,
    abgelaufen: users.filter(u => u.status === 'abgelaufen').length,
    kunde: users.filter(u => u.status === 'kunde').length,
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 10000,
          padding: '0.75rem 1.25rem', borderRadius: '8px',
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          fontSize: '0.875rem', fontWeight: 500,
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <UserPlus size={22} />
            Trial-Nutzer & Onboarding
          </h2>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
            Registrierungen verwalten und Onboarding-E-Mails konfigurieren
          </p>
        </div>
        <button
          onClick={loadData}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.5rem 1rem', fontSize: '0.8125rem',
            border: '1px solid var(--border-light, #e2e8f0)',
            borderRadius: '6px', background: 'var(--bg-card)', cursor: 'pointer',
            color: 'var(--text-secondary)',
          }}
        >
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Gesamt', value: stats.total, color: 'var(--accent-slate, #5b7c8d)' },
          { label: 'Trial aktiv', value: stats.trial_aktiv, color: '#10b981' },
          { label: 'Unbestätigt', value: stats.unbestaetigt, color: '#f59e0b' },
          { label: 'Abgelaufen', value: stats.abgelaufen, color: '#94a3b8' },
          { label: 'Kunde', value: stats.kunde, color: '#3b82f6' },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '1rem', borderRadius: '8px',
            border: '1px solid var(--border-light, #e2e8f0)',
            background: 'var(--bg-card)',
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* View-Toggle */}
      <div style={{
        display: 'flex', gap: '0', marginBottom: '1rem',
        border: '1px solid var(--border-light, #e2e8f0)',
        borderRadius: '8px', overflow: 'hidden', width: 'fit-content',
      }}>
        {[
          { id: 'users', label: 'Nutzer', icon: UserPlus },
          { id: 'templates', label: 'E-Mail-Templates', icon: Mail },
        ].map(view => {
          const Icon = view.icon;
          return (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.5rem 1.25rem', fontSize: '0.8125rem', fontWeight: activeView === view.id ? 600 : 400,
                border: 'none', cursor: 'pointer',
                background: activeView === view.id ? 'var(--accent-slate, #5b7c8d)' : 'var(--bg-card)',
                color: activeView === view.id ? '#fff' : 'var(--text-secondary)',
              }}
            >
              <Icon size={14} />
              {view.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeView === 'users' && (
        <div>
          {/* Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            {[
              { id: 'all', label: `Alle (${stats.total})` },
              { id: 'trial_aktiv', label: `Aktiv (${stats.trial_aktiv})` },
              { id: 'unbestaetigt', label: `Unbestätigt (${stats.unbestaetigt})` },
              { id: 'abgelaufen', label: `Abgelaufen (${stats.abgelaufen})` },
              { id: 'kunde', label: `Kunde (${stats.kunde})` },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{
                  padding: '0.375rem 0.75rem', fontSize: '0.8125rem',
                  border: '1px solid var(--border-light, #e2e8f0)',
                  borderRadius: '6px', cursor: 'pointer',
                  background: filter === f.id ? 'var(--accent-slate, #5b7c8d)' : 'var(--bg-card)',
                  color: filter === f.id ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* User-Liste */}
          {filteredUsers.length === 0 ? (
            <div style={{
              padding: '3rem', textAlign: 'center',
              color: 'var(--text-secondary)', fontSize: '0.875rem',
              border: '1px solid var(--border-light)', borderRadius: '8px',
            }}>
              Keine Nutzer gefunden
            </div>
          ) : (
            filteredUsers.map(user => (
              <UserCard key={user.user_sid} user={user} onAction={handleAction} />
            ))
          )}
        </div>
      )}

      {activeView === 'templates' && templates && (
        <TemplateEditor templates={templates} onSave={handleSaveTemplates} saving={saving} />
      )}
    </div>
  );
};

export default TrialUsersIndex;
